import useEntities from 'new/state/entities/repository/useEntities';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import getFlowItems from 'js/components/pipelines/ExpandedPipelines/utils/getFlowItems';
import { filterWebhookResponseSourcesFromItems } from 'js/components/pipelines/FlowNodes/utils';
import useTriggeringFlows from 'js/components/pipelines/ExpandedPipelines/hooks/useTriggeringFlows';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { matchPath, useLocation } from 'react-router-dom';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';

const defaultOption = {
  ignoreProject: false,
};

export default function usePipelinesItems(flows, { ignoreProject } = defaultOption) {
  const branches = useSelector((state: any) => state.pipelines.branches);
  const metaInfoById = useSelector((state: any) => state.paging?.metaInfoById ?? {});
  const { data: entities } = useEntities();
  // Use lastUpdated to detect when pipeline data changes
  // This ensures pipelineItems recalculates when linked flows are fetched
  const lastUpdated = useSelector((state: any) => state.pipelines?.lastUpdated);
  const { sources, dataSets, sinks, flowNodes, projects: projectMap } = entities;
  const {
    triggerFlows,
    triggeredFlows,
    triggeringMap,
    mapTriggeringFlows,
    mapFlowLinks,
    linksMap,
    linkedFlows,
  } = useTriggeringFlows(flows);
  const { data: useExpanded } = useHashedState('flows.useExpanded');
  const ragFlowId = useRagFlowId();
  const location = useLocation();

  const isPipelineListView = matchPath(
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}`,
    location.pathname,
  )?.isExact;
  const isShowingRagFlow = !!ragFlowId;

  // Flows that need to be removed of pipeline items map
  // (because they're going to be rendered by the flow_triggers property)

  return useMemo(() => {
    const newLinksMap = Object.assign({}, linksMap);

    flows.forEach((flow) => {
      delete newLinksMap[flow.flow_node_id];
    });

    const flowsArr = [...flows];

    const pipelineItems = flowsArr.reduce((map, f) => {
      const source = sources[f.data_source_id];
      const flowEntity = getResourceEntity(flowNodes[source?.origin_node_id], entities, true, [
        'name',
        'description',
      ]);
      const flow = { ...flowEntity, ...f, resource_type: 'flow' };
      const rawItems = getFlowItems(flow, { entities, branches });
      const items = filterWebhookResponseSourcesFromItems(rawItems);
      // const isLink = linkedEntities.some(
      //   (e: any) => e.flow_node_id === flow.flow_node_id
      // );

      Object.values(items).forEach((item) => {
        if (
          item.node_type === 'data_retriever_holder' &&
          !useExpanded &&
          isPipelineListView &&
          isShowingRagFlow
        ) {
          // show full flows starting from a source
          const linkedFlowIds = flowNodes[item.origin_node_id]?.linked_flows?.filter(
            (linkedFlowId) => !flowNodes[linkedFlowId]?.data_retriever_ids?.length,
          );
          const linkedBranchItems =
            linkedFlowIds?.map((linkedFlowId) => branches[linkedFlowId]?.items) || [];

          // show data set retrievers
          const linkedDataSetIds =
            flowNodes[item.origin_node_id]?.linked_flows?.reduce((acc, linkedFlowId) => {
              const linkedFlowNode = flowNodes[linkedFlowId];
              if (linkedFlowNode?.data_retriever_ids?.length) {
                acc.push(...linkedFlowNode.data_retriever_ids);
              }

              return acc;
            }, []) || [];

          linkedDataSetIds.forEach((dataSetId) => {
            const { flow_node_id } = dataSets[dataSetId];
            const dataSetBranch = [flow_node_id];
            const reranker = flowNodes[flow_node_id]?.children?.find(
              (childId) => flowNodes[childId]?.node_type === 'reranker',
            );

            if (reranker) {
              dataSetBranch.push(reranker);
            }

            linkedBranchItems.push(dataSetBranch);
          });

          item.retriever_flows = linkedBranchItems.map((retrieverBranch) =>
            mapValues(pick(flowNodes, retrieverBranch), (flowNode) =>
              getResourceEntity(flowNode, entities),
            ),
          );
        }

        if (item.node_type === 'llm_processor') {
          const response = {
            ...item,
            flow_node_id: `${item.flow_node_id}-response`,
            node_type: 'query_response',
          };
          item.children = [response.flow_node_id];
          items[response.flow_node_id] = response;
        }
      });

      if (triggerFlows.includes(flow.flow_node_id)) return map;

      if (triggeredFlows.includes(flow.flow_node_id)) {
        mapTriggeringFlows(flow, false);
      }

      map[flow.flow_node_id] = {
        ...flow,
        renderProjectLabel:
          triggeredFlows.includes(flow.flow_node_id) ||
          // linkedFlows.includes(flow.flow_node_id) ||
          triggerFlows.includes(flow.flow_node_id),
        project: projectMap[flow.project_id],
        resource_type: 'flow',
        items,
      };

      return map;
    }, {});

    const orderItems = Object.values(pipelineItems)
      .filter((f: any) => !linkedFlows.includes(f.origin_node_id))
      .sort((a: any, b: any) => {
        if (flows.some((f) => f.origin_node_id === a.origin_node_id)) return -1;
        if (flows.some((f) => f.origin_node_id === b.origin_node_id)) return 1;
        return 0;
      })
      .map((f: any) => f.flow_node_id);

    return {
      triggerFlows,
      triggeringMap,
      linksMap,
      linkedFlows,
      triggeredFlows,
      pipelineItems,
      projects: Object.values(projectMap),
      orderItems,
    };
  }, [
    sources,
    dataSets,
    sinks,
    flowNodes,
    branches,
    projectMap,
    metaInfoById,
    flows,
    /**
     * ISSUE: Linked Flows Not Updating When Fetched
     *
     * Problem: When linked flows were fetched, pipelineItems didn't recalculate because
     * linksMap wasn't in the dependency array. This caused:
     * - Newly fetched linked flows not appearing in the flow canvas
     * - Stale data in pipelineItems
     * - Layout not recalculating when linked flows became available
     *
     * Solution: Add linksMap, triggeringMap, and lastUpdated to the dependency array.
     * This ensures pipelineItems recalculates when:
     * - Linked flows are fetched (linksMap changes)
     * - Triggering flows are updated (triggeringMap changes)
     * - Pipeline data is refreshed (lastUpdated changes)
     */
    linksMap, // Include linksMap so useMemo recalculates when linked flows are fetched
    triggeringMap, // Include triggeringMap for consistency
    lastUpdated, // Include lastUpdated to detect pipeline updates
    isPipelineListView,
    isShowingRagFlow,
    useExpanded,
    ragFlowId,
    location.pathname,
  ]);
}
